import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { PageHeader } from '@/components/layout/PageHeader';
import { StatusBadge } from '@/components/common/StatusBadge';
import { contractRequestAPI, appointmentAPI } from '@/lib/api';
import { isKRRole, formatDate, formatDateTime, CONTRACT_STATUS_ORDER, getErrorMessage, formatPhone } from '@/lib/utils';
import { ReleaseFollowUpCard } from '@/components/contracts/ReleaseFollowUpCard';
import { ContractTimeline } from '@/components/contracts/ContractTimeline';
import { ContractHealthScore } from '@/components/contracts/ContractHealthScore';
import { CreateFollowupDialog } from '@/components/crm/CreateFollowupDialog';
import { NeedsReviewBadge } from '@/components/contracts/NeedsReviewBadge';
import InboundNotesPanel from '@/components/common/InboundNotesPanel';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Textarea } from '@/components/ui/textarea';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import {
  User,
  Building2,
  FileText,
  Clock,
  CheckCircle,
  AlertCircle,
  Hash,
  Loader2,
  ArrowLeft
} from 'lucide-react';

export default function RequestDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  // If the user came here from an agent profile (or any specific entry point)
  // we honor it for "Back" so they return exactly where they were instead of
  // landing on the generic requests list.
  const backHint = location.state?.from || null;
  const { user } = useAuth();
  const [requestData, setRequestData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showStatusDialog, setShowStatusDialog] = useState(false);
  const [showAppointmentDialog, setShowAppointmentDialog] = useState(false);
  const [showCreateFollowup, setShowCreateFollowup] = useState(false);
  const [statusForm, setStatusForm] = useState({ status: '', comment: '', status_reason: '', writing_number: '' });
  const [appointmentForm, setAppointmentForm] = useState({
    writing_number: '',
    appointment_status: 'Approved',
    appointed_date: '',
    notes_internal: ''
  });
  const [editingAppointment, setEditingAppointment] = useState(false);

  const isKR = isKRRole(user);

  useEffect(() => {
    loadRequestData();
  }, [id]);

  // Phase 24.1 — deep-link support: ?tab=timeline scrolls to the Timeline card.
  useEffect(() => {
    const params = new URLSearchParams(location.search || '');
    if (params.get('tab') === 'timeline') {
      const el = document.getElementById('contract-timeline');
      if (el) setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'start' }), 300);
    }
  }, [location.search, requestData]);

  const loadRequestData = async () => {
    try {
      const response = await contractRequestAPI.get(id);
      setRequestData(response.data);
    } catch (error) {
      toast.error('Failed to load request details');
      navigate('/requests');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (e) => {
    e.preventDefault();
    // Phase 27.25 — when moving a request into "Approved" we require a
    // writing number in the same dialog. Historically this was a two-step
    // flow (change status, then click a second "Enter Writing Number"
    // button) which agents/admins found unintuitive. We keep the
    // appointment upsert side-effect inside the same submit so a single
    // click delivers both.
    const isApproving = statusForm.status === 'Approved';
    const wn = (statusForm.writing_number || '').trim();
    if (isApproving && !wn) {
      toast.error('Writing number is required to mark a contract as Approved');
      return;
    }
    try {
      await contractRequestAPI.updateStatus(id, {
        status: statusForm.status,
        comment: statusForm.comment,
        status_reason: statusForm.status_reason,
      });
      if (isApproving) {
        const existingAppt = requestData?.appointment;
        if (existingAppt?.appointment_id) {
          await appointmentAPI.update(existingAppt.appointment_id, {
            writing_number: wn,
            appointment_status: 'Approved',
          });
        } else {
          await appointmentAPI.create({
            request_id: id,
            npn: requestData.request.npn,
            carrier_id: requestData.request.carrier_id,
            writing_number: wn,
            appointment_status: 'Approved',
          });
        }
      }
      toast.success('Status updated successfully');
      setShowStatusDialog(false);
      loadRequestData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to update status'));
    }
  };

  const handleCreateAppointment = async (e) => {
    e.preventDefault();
    try {
      if (editingAppointment && requestData?.appointment?.appointment_id) {
        await appointmentAPI.update(requestData.appointment.appointment_id, {
          writing_number: appointmentForm.writing_number,
          appointment_status: appointmentForm.appointment_status,
          appointed_date: appointmentForm.appointed_date || null,
          notes_internal: appointmentForm.notes_internal,
        });
        toast.success('Appointment updated successfully');
      } else {
        await appointmentAPI.create({
          request_id: id,
          npn: requestData.request.npn,
          carrier_id: requestData.request.carrier_id,
          ...appointmentForm,
        });
        toast.success('Appointment created successfully');
      }
      setShowAppointmentDialog(false);
      setEditingAppointment(false);
      loadRequestData();
    } catch (error) {
      toast.error(getErrorMessage(error, editingAppointment ? 'Failed to update appointment' : 'Failed to create appointment'));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  if (!requestData) return null;

  const { request, workflows, agent, carrier, contract_type, level, agency, appointment, current_status } = requestData;

  // Reason attached to the most recent status change — surfaced next to the
  // status badge so the "why" is visible at a glance without opening the timeline.
  const latestWorkflow = workflows && workflows.length ? workflows[workflows.length - 1] : null;
  const currentStatusReason = latestWorkflow?.status_reason || null;

  return (
    <div data-testid="request-detail-page">
      <PageHeader 
        title="Contract Details"
        breadcrumbs={[
          { label: 'Dashboard', href: '/dashboard' },
          ...(backHint ? [{ label: backHint.label || 'Back', href: backHint.url }] : [{ label: 'Contracts', href: '/requests' }]),
          { label: `Contract ${request.request_id.slice(0, 8)}...`, href: `/requests/${id}` }
        ]}
        actions={
          <div className="flex items-center gap-2">
            {isKR && (
              <Button
                variant="default"
                onClick={() => setShowCreateFollowup(true)}
                data-testid="contract-create-followup-btn"
              >
                Create Follow-up
              </Button>
            )}
            <Button
              variant="outline"
              onClick={() => navigate(backHint?.url || '/requests')}
              data-testid="request-back-btn"
            >
              <ArrowLeft size={16} className="mr-2" />
              {backHint?.label ? `Back to ${backHint.label}` : 'Back to Contracts'}
            </Button>
          </div>
        }
      />
      
      <div className="page-content">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Info */}
          <div className="lg:col-span-2 space-y-6">
            {/* Request Summary */}
            <Card>
              <CardHeader>
                <div className="flex items-start justify-between">
                  <CardTitle className="text-lg font-heading">Contract Summary</CardTitle>
                  <div className="flex flex-col items-end gap-1">
                    <StatusBadge status={current_status} />
                    {currentStatusReason && (
                      <span
                        className="text-xs text-slate-500 italic max-w-[220px] text-right"
                        data-testid="current-status-reason"
                      >
                        Reason: {currentStatusReason}
                      </span>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Phase 24.3 — Needs Review banner for legacy rows */}
                <NeedsReviewBadge
                  contract={request}
                  variant="panel"
                  onReviewed={() => loadRequestData()}
                />
                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <Label className="text-slate-500">Carrier</Label>
                    <p className="font-medium text-slate-900">{carrier?.carrier_name || '-'}</p>
                  </div>
                  <div>
                    <Label className="text-slate-500">Contract Type</Label>
                    <p className="font-medium text-slate-900">{contract_type?.contract_type_name || '-'}</p>
                  </div>
                  <div>
                    <Label className="text-slate-500">Requested Level</Label>
                    <p className="font-medium text-slate-900">{level?.level_name || '-'}</p>
                  </div>
                  <div>
                    <Label className="text-slate-500">Agency</Label>
                    <p className="font-medium text-slate-900">{agency?.agency_name || '-'}</p>
                  </div>
                </div>
                
                {request.request_notes && (
                  <>
                    <Separator />
                    <div>
                      <Label className="text-slate-500">Request Notes</Label>
                      <p className="text-slate-700 mt-1">{request.request_notes}</p>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>

            {/* Appointment Info (if exists) */}
            {appointment && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg font-heading flex items-center gap-2">
                    <CheckCircle size={20} className="text-emerald-500" />
                    Appointment Details
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-6">
                    <div>
                      <Label className="text-slate-500">Status</Label>
                      <p><StatusBadge status={appointment.appointment_status} /></p>
                    </div>
                    <div>
                      <Label className="text-slate-500">Writing Number</Label>
                      <p className="font-mono font-medium text-slate-900">
                        {appointment.writing_number || '-'}
                      </p>
                    </div>
                    <div>
                      <Label className="text-slate-500">Appointed Date</Label>
                      <p className="font-medium text-slate-900">
                        {appointment.appointed_date ? formatDate(appointment.appointed_date) : '-'}
                      </p>
                    </div>
                    {appointment.terminated_date && (
                      <div>
                        <Label className="text-slate-500">Terminated Date</Label>
                        <p className="font-medium text-slate-900">
                          {formatDate(appointment.terminated_date)}
                        </p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Contract Health Score */}
            <ContractHealthScore requestId={request.request_id} />

            {/* Release Follow-Up panel — shown if data exists or KR can create */}
            <ReleaseFollowUpCard
              request={request}
              isKR={isKR}
              onChange={loadRequestData}
            />

            {/* Unified Contract Timeline (Phase 24.1 — includes predecessor chain events) */}
            <div id="contract-timeline" data-testid="contract-timeline-section">
              <ContractTimeline requestId={request.request_id} />
            </div>

            {/* Phase 27.53.F — Notes + Email-to-Note */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg font-heading">Notes</CardTitle>
              </CardHeader>
              <CardContent>
                <InboundNotesPanel
                  entityId={request.request_id}
                  isKR={isKR}
                  api={{
                    listNotes: contractRequestAPI.listNotes,
                    addNote: contractRequestAPI.addNote,
                    updateNoteVisibility: contractRequestAPI.updateNoteVisibility,
                    getInboundAddress: contractRequestAPI.getInboundAddress,
                    reply: contractRequestAPI.reply,
                  }}
                  inboundHint="Contract inbox — CC or BCC to log an email as an internal note"
                  testidPrefix="contract"
                />
              </CardContent>
            </Card>

            {/* KR Actions — always available so admins can edit status from
                inside the detail page, matching the Update button in the
                Requests Queue table. */}
            {isKR && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg font-heading">Admin Actions</CardTitle>
                </CardHeader>
                <CardContent className="flex flex-wrap gap-4">
                  <Button onClick={() => {
                    setStatusForm({
                      status: current_status,
                      comment: '',
                      status_reason: '',
                      writing_number: appointment?.writing_number || '',
                    });
                    setShowStatusDialog(true);
                  }} data-testid="change-status-btn">
                    Update Status
                  </Button>
                  {current_status === 'Approved' && !appointment && (
                    <Button
                      variant="outline"
                      onClick={() => {
                        setEditingAppointment(false);
                        setAppointmentForm({
                          writing_number: '',
                          appointment_status: 'Approved',
                          appointed_date: '',
                          notes_internal: '',
                        });
                        setShowAppointmentDialog(true);
                      }}
                      data-testid="create-appointment-btn"
                    >
                      <Hash size={16} className="mr-2" />
                      Enter Writing Number
                    </Button>
                  )}
                  {appointment && (
                    <Button
                      variant="outline"
                      onClick={() => {
                        setEditingAppointment(true);
                        setAppointmentForm({
                          writing_number: appointment.writing_number || '',
                          appointment_status: appointment.appointment_status || 'Approved',
                          appointed_date: appointment.appointed_date ? appointment.appointed_date.slice(0, 10) : '',
                          notes_internal: appointment.notes_internal || '',
                        });
                        setShowAppointmentDialog(true);
                      }}
                      data-testid="edit-appointment-btn"
                    >
                      <Hash size={16} className="mr-2" />
                      Edit Writing Number
                    </Button>
                  )}
                </CardContent>
              </Card>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Agent Info */}
            <Card>
              <CardHeader>
                <CardTitle className="text-sm font-heading flex items-center gap-2">
                  <User size={18} className="text-slate-400" />
                  Agent Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className="text-slate-500 text-xs">Name</Label>
                  <p className="font-medium text-slate-900">
                    {agent?.first_name} {agent?.last_name}
                  </p>
                </div>
                <div>
                  <Label className="text-slate-500 text-xs">NPN</Label>
                  <p className="font-mono text-sm text-slate-900">{agent?.npn}</p>
                </div>
                {agent?.email && (
                  <div>
                    <Label className="text-slate-500 text-xs">Email</Label>
                    <p className="text-sm text-slate-900">{agent.email}</p>
                  </div>
                )}
                {agent?.phone && (
                  <div>
                    <Label className="text-slate-500 text-xs">Phone</Label>
                    <p className="text-sm text-slate-900">{formatPhone(agent.phone)}</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Timeline */}
            <Card>
              <CardHeader>
                <CardTitle className="text-sm font-heading flex items-center gap-2">
                  <Clock size={18} className="text-slate-400" />
                  Status Timeline
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="relative">
                  {workflows.map((wf, index) => (
                    <div key={wf.workflow_id} className="relative pl-6 pb-6 last:pb-0">
                      {/* Line */}
                      {index < workflows.length - 1 && (
                        <div className="absolute left-[9px] top-4 bottom-0 w-0.5 bg-slate-200" />
                      )}
                      {/* Dot */}
                      <div className={`absolute left-0 top-1 w-[18px] h-[18px] rounded-full border-2 bg-white ${
                        index === workflows.length - 1 ? 'border-sky-500' : 'border-slate-300'
                      }`} />
                      {/* Content */}
                      <div>
                        <StatusBadge status={wf.status} />
                        <p className="text-xs text-slate-500 mt-1">
                          {formatDateTime(wf.changed_at)}
                        </p>
                        {wf.status_reason && (
                          <p className="text-xs text-slate-600 mt-1 italic">Reason: {wf.status_reason}</p>
                        )}
                        {wf.comment && (
                          <p className="text-sm text-slate-600 mt-1">{wf.comment}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Status Change Dialog */}
      <Dialog open={showStatusDialog} onOpenChange={setShowStatusDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading">Update Request Status</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleStatusChange}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>New Status</Label>
                <Select
                  value={statusForm.status}
                  onValueChange={(value) => setStatusForm(prev => ({ ...prev, status: value }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CONTRACT_STATUS_ORDER.map(status => (
                      <SelectItem key={status} value={status}>
                        {status.replace(/_/g, ' ')}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Comment</Label>
                <Textarea
                  value={statusForm.comment}
                  onChange={(e) => setStatusForm(prev => ({ ...prev, comment: e.target.value }))}
                  placeholder="Add a note..."
                  rows={3}
                />
              </div>
              <div className="space-y-2">
                <Label>Status Reason</Label>
                <Input
                  value={statusForm.status_reason}
                  onChange={(e) => setStatusForm(prev => ({ ...prev, status_reason: e.target.value }))}
                  placeholder="Reason for this status change (optional)"
                  data-testid="status-reason-input"
                />
              </div>
              {/* Phase 27.25 — writing number captured in-place when
                  approving a contract, so it's a single-click flow. */}
              {statusForm.status === 'Approved' && (
                <div className="space-y-2" data-testid="status-writing-number-block">
                  <Label>
                    Writing Number
                    <span className="text-red-500"> *</span>
                  </Label>
                  <Input
                    value={statusForm.writing_number}
                    onChange={(e) => setStatusForm(prev => ({ ...prev, writing_number: e.target.value }))}
                    placeholder="Enter writing number"
                    data-testid="status-writing-number-input"
                    required
                  />
                  {requestData?.appointment?.writing_number && (
                    <p className="text-[11px] text-slate-500">
                      Existing writing number:{' '}
                      <span className="font-mono">{requestData.appointment.writing_number}</span>
                      {' '}— changing it here will overwrite the appointment record.
                    </p>
                  )}
                </div>
              )}
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowStatusDialog(false)}>
                Cancel
              </Button>
              <Button type="submit">Update Status</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Create / Edit Appointment Dialog */}
      <Dialog open={showAppointmentDialog} onOpenChange={(v) => {
        setShowAppointmentDialog(v);
        if (!v) setEditingAppointment(false);
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading">
              {editingAppointment ? 'Edit Appointment' : 'Create Appointment'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateAppointment}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Writing Number</Label>
                <Input
                  value={appointmentForm.writing_number}
                  onChange={(e) => setAppointmentForm(prev => ({ ...prev, writing_number: e.target.value }))}
                  placeholder="Enter writing number"
                  data-testid="writing-number-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Appointed Date</Label>
                <Input
                  type="date"
                  value={appointmentForm.appointed_date}
                  onChange={(e) => setAppointmentForm(prev => ({ ...prev, appointed_date: e.target.value }))}
                  data-testid="appointed-date-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Notes</Label>
                <Textarea
                  value={appointmentForm.notes_internal}
                  onChange={(e) => setAppointmentForm(prev => ({ ...prev, notes_internal: e.target.value }))}
                  placeholder="Internal notes..."
                  rows={3}
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowAppointmentDialog(false)}>
                Cancel
              </Button>
              <Button type="submit" data-testid="create-appointment-submit">
                {editingAppointment ? 'Save Changes' : 'Create Appointment'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Phase 23.1 — Create Follow-up dialog (linked to this contract request) */}
      {isKR && (
        <CreateFollowupDialog
          open={showCreateFollowup}
          onOpenChange={setShowCreateFollowup}
          source={{
            source_type: 'contract_request',
            source_id: request.request_id,
            subject_type: 'agent',
            subject_id: agent?.npn,
          }}
          defaultAssigneeAgencyId={agent?.agency_id}
        />
      )}
    </div>
  );
}
